import React from "react";
import ServiceLandingPage, {
  InternalLink,
} from "../components/ServiceLandingPage";
import { COMPANY } from "../content";
import AISOBlock from "../components/AISOBlock";
import MotionMedia from "../components/MotionMedia";
import { videoJsonLd } from "../lib/videoSchema";

const HERO_IMAGE = "/projects/tanvir-vip-lounge-1.webp";

const SCOPE = [
  {
    name: "Bespoke sofa design",
    desc: "Sectionals, modular L-shapes, deep loungers, classic three-seaters and statement curved sofas — designed to fit your room dimensions, ceiling height and traffic flow.",
  },
  {
    name: "Frame, suspension & cushion engineering",
    desc: "Kiln-dried solid hardwood frames, eight-way hand-tied or sinuous suspension, and feather-down or high-resilience foam cushions tuned to the firmness you actually want to sit on.",
  },
  {
    name: "Premium fabrics & leathers",
    desc: "Bouclé, chenille, linen, velvet, performance fabrics, full-grain Italian leather and stain-resistant indoor-outdoor weaves — sourced from leading mills and presented as physical samples.",
  },
  {
    name: "Detailing & finishing",
    desc: "Hand-stitched piping, channel tufting, deep button work, contrast welts and brass or bronze sabots — all finished by hand at our Sharjah upholstery atelier.",
  },
  {
    name: "Re-upholstery & restoration",
    desc: "Re-spring, re-foam and re-upholster existing sofas, headboards, dining chairs and benches — extending the life of pieces you already love, in fabrics that suit your new space.",
  },
  {
    name: "Delivery, install & after-care",
    desc: "Climate-controlled delivery, in-room placement, leveling and a written defects warranty on the frame, suspension and stitching — backed by adjustment visits in the first year.",
  },
];

// "From Frame to Finish" — the 4-step custom-sofa process the user requested.
// Images sourced from existing on-site upholstery & design assets so they're
// fully representative; easy to replace later by editing the `image` field.
const PROCESS = [
  {
    n: "01",
    title: "Measurement & proportion planning",
    body: "We visit the room to measure floor-plan, ceiling height, traffic flow, doorway and lift access — and translate it into seat depth, back angle and cushion height that suit how you actually sit, not just how the sofa looks in a render.",
    image: "/media/team-projects/time-furniture-site-planning-project-coordination.webp",
    imageAlt: "On-site discussion and project coordination during a Time Furniture fit-out",
  },
  {
    n: "02",
    title: "Frame construction",
    body: "Kiln-dried solid hardwood frames cut, joined and reinforced at our Sharjah atelier. Eight-way hand-tied or heavy-gauge sinuous suspension is fitted before any foam or fabric — the part of the sofa you can't see is the part that lasts.",
    image: "/media/revision-oct5/time-furniture-woodworking-craftsmanship.webp",
    imageAlt: "Woodworking craftsmanship — hand-finishing a timber detail",
  },
  {
    n: "03",
    title: "Foam, comfort & angle testing",
    body: "Cushion fillings are built up — high-resilience foam cores, feather-down wraps, fibre layers — and tested in-house for firmness, seat depth and back angle. We adjust until the sit-feel matches the brief, before fabric goes anywhere near it.",
    image: "/media/owner-projects/custom-curved-sofa-caramel-upholstery-time-furniture.webp",
    imageAlt: "Custom curved sofa in caramel upholstery with ivory cushions",
  },
  {
    n: "04",
    title: "Upholstery & final finishing",
    body: "Selected fabric or full-grain leather is cut, tailored and hand-stitched onto the frame — piping, channel tufting and contrast welts finished by hand. Final inspection, climate-controlled delivery and in-room placement complete the process.",
    image: "/media/revision-oct6/time-furniture-upholstery-craftsman-clean-workshop.webp",
    imageAlt: "Time Furniture upholstery craftsman with over 25 years of service",
  },
];

const FAQS = [
  {
    q: "How long does a custom sofa take to make in Dubai?",
    a: "A custom sofa in Dubai typically takes 5–8 weeks from approved design and fabric sign-off to in-home delivery. Curved or fully-tufted designs in imported fabrics or full-grain leather can take 8–10 weeks. We provide a fixed delivery date in every contract.",
  },
  {
    q: "Can I choose any fabric, leather or colour?",
    a: "Yes. We hold an extensive fabric and leather library — bouclé, chenille, linen, velvet, performance fabrics, full-grain leather and indoor-outdoor weaves — and we can also work with COM (client-own-material). Physical samples are presented before sign-off so you see the texture and colour in your own light.",
  },
  {
    q: "What about the frame, springs and cushion fillings?",
    a: "All sofas are built on kiln-dried solid hardwood frames with either eight-way hand-tied or heavy-gauge sinuous suspension, depending on the design. Cushion fillings range from high-resilience foam (firmer, more structured) to feather-down wraps over foam cores (softer, more lounge-like) — tuned to the firmness you actually want.",
  },
  {
    q: "Do you re-upholster existing sofas or only build new ones?",
    a: "Both. Time Furniture's atelier handles full re-upholstery — re-spring, re-foam, re-cover — for sofas, headboards, dining chairs, benches and bedheads. We can also re-design the existing piece (deeper seat, lower back, new arms) rather than just re-cover it.",
  },
  {
    q: "Do you provide a warranty on custom sofas?",
    a: "Yes. Time Furniture provides a written defects warranty covering the frame, suspension and stitching. Cushion fillings and fabric are also serviceable — we offer paid re-feathering and re-covering for the life of the piece, so the sofa never has to be thrown away.",
  },
];

const WHY = [
  {
    t: "Our own upholstery atelier",
    d: "Frames, suspension, fillings and tailoring are all done at our Sharjah workshop — no third-party upholsterer in the chain.",
  },
  {
    t: "Sit before you commit",
    d: "Cushion firmness, seat depth and back angle can be sampled and adjusted before final build — no surprises on delivery day.",
  },
  {
    t: "Built to be re-loved",
    d: "Designed for re-upholstery and re-feathering — the sofa you commission today can be re-covered in five years, not replaced.",
  },
];

export default function CustomSofasDubai() {
  return (
    <ServiceLandingPage
      testid="custom-sofas-dubai-page"
      whatsappText={COMPANY.whatsappSofasText}
      pageTitle="Custom Sofas Dubai | Bespoke Upholstery & Re-Upholstery UAE — Time Furniture"
      metaDescription="Custom sofas Dubai and upholstery furniture UAE — bespoke sectionals, curved sofas and lounge seating hand-built at our Sharjah atelier. Custom furniture manufacturer UAE: hardwood frames, eight-way hand-tied suspension, premium fabrics. Re-upholstery available."
      canonical="https://timefurniture.ae/custom-sofas-dubai"
      ogImage={HERO_IMAGE}
      serviceSchemaName="Custom Sofas & Upholstery — Dubai & UAE"
      breadcrumbLabel="Custom Sofas Dubai"
      eyebrow="Custom Sofas · Dubai & UAE"
      h1="Custom Sofas &"
      h1Accent="Upholstery"
      heroImage={HERO_IMAGE}
      heroImageAlt="Custom sofa and lounge seating by Time Furniture in a private Dubai VIP lounge — bespoke upholstery"
      heroMedia={
        <MotionMedia
          mediaKey="sofa-hero"
          alt="Sofa craftsmanship film — cutting ply, frame assembly, foam layers, sewing and upholstery to a finished sofa"
          mode="hero"
          aspectClass="h-full w-full"
          testid="sofas-hero-media"
          eager
        />
      }
      extraJsonLd={[
        videoJsonLd({
          key: "sofa-hero",
          name: "Custom sofa craftsmanship — from timber frame to tailored sofa",
          description:
            "Inside the Time Furniture atelier: cutting ply, assembling the hardwood frame, layering foam, sewing covers and fastening upholstery through to the finished custom sofa.",
          seconds: 30,
        }),
      ]}
      intro="Time Furniture is a custom sofa maker in Dubai — designing and hand-building bespoke seating at our own upholstery atelier in Sharjah since 1993. Sectional sofas, curved loungers, deep three-seaters, channel-tufted classics and modular living-room systems are built on kiln-dried hardwood frames with eight-way hand-tied or sinuous suspension, foam-and-feather cushion stacks, and your choice of bouclé, chenille, linen, velvet, full-grain leather or performance fabric. We consider seat depth, back angle, cushion density, fabric type, and room proportion before manufacturing custom sofas and loose furniture — so the piece you commission fits the room, the use, and the way you actually sit. Beyond new pieces, we also offer full re-upholstery — re-spring, re-foam and re-cover — for sofas, headboards and dining chairs you already own."
      scope={SCOPE}
      process={PROCESS}
      whyChoose={WHY}
      faqs={FAQS}
      ctaHeading="Ready to design your"
      ctaAccent="custom sofa"
      ctaSubtext="Book a free home visit. We'll measure the room, walk through fabric and frame options, and reply with a transparent quote — frame, suspension and fabric priced separately."
      aisoBlock={
        <>
          {/* RECENT WORK — authentic owner-supplied sofa photographs */}
          <section data-testid="sofas-recent-work" className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
            <p className="eyebrow mb-4">Recent work, built by us</p>
            <h2 className="font-serif-display text-3xl md:text-4xl text-[#1C110C] max-w-2xl">
              Sofas &amp; lounge seating from our atelier.
            </h2>
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {[
                { src: "/media/owner-projects/custom-curved-sofa-caramel-upholstery-time-furniture.webp", w: 2000, h: 1500, alt: "Custom curved sofa in caramel upholstery hand-built by Time Furniture", cap: "Curved sofa · caramel upholstery", wide: true },
                { src: "/media/owner-projects/custom-villa-sofas-neutral-upholstery-time-furniture.webp", w: 1280, h: 960, alt: "Custom villa sofas in neutral upholstery by Time Furniture", cap: "Villa lounge seating" },
                { src: "/media/owner-projects/custom-cream-sofa-hospitality-lounge-time-furniture.webp", w: 1500, h: 2000, alt: "Custom cream sofa in a hospitality lounge by Time Furniture", cap: "Hospitality lounge sofa" },
                { src: "/media/owner-projects/custom-upholstered-armchair-curved-back-time-furniture.webp", w: 1500, h: 2000, alt: "Custom upholstered armchair with curved back by Time Furniture", cap: "Curved-back armchair" },
                { src: "/media/owner-projects/custom-lounge-chair-tailored-upholstery-time-furniture.webp", w: 1500, h: 2000, alt: "Custom lounge chair with tailored upholstery by Time Furniture", cap: "Tailored lounge chair" },
              ].map((g, i) => (
                <figure key={g.src} className={`group overflow-hidden bg-[#EDE1C8] ${g.wide ? "col-span-2 lg:col-span-1" : ""}`}>
                  <img
                    src={g.src}
                    width={g.w}
                    height={g.h}
                    loading="lazy"
                    decoding="async"
                    alt={g.alt}
                    data-testid={`sofas-recent-img-${i}`}
                    className="w-full aspect-[4/3] object-cover object-center transition-transform duration-500 group-hover:scale-105"
                  />
                  <figcaption className="px-3 py-2.5 text-[10px] tracking-[0.14em] uppercase text-[#3E2A22] truncate">{g.cap}</figcaption>
                </figure>
              ))}
            </div>
          </section>
          <AISOBlock
          testid="sofas-aiso"
          audience={[
            "Villa owners commissioning lounge or majlis seating",
            "Apartment owners replacing off-the-shelf sofas with bespoke pieces",
            "Holiday home and Airbnb investors needing durable, re-coverable furniture",
            "Hospitality and commercial clients (lounges, lobbies, clinics)",
          ]}
          whatWeDo="We design, hand-build and deliver bespoke sofas and loose furniture in Dubai — kiln-dried hardwood frames, eight-way hand-tied or sinuous suspension, foam-and-feather cushion stacks, and your choice of bouclé, linen, velvet, leather or performance fabric. Re-upholstery of existing sofas, headboards and dining chairs is also handled at our Sharjah atelier."
          scope={[
            "Site measurement & proportion planning",
            "Frame construction (kiln-dried hardwood)",
            "Suspension & cushion engineering",
            "Fabric / leather selection & sampling",
            "Hand-stitched upholstery & finishing",
            "Climate-controlled delivery & install",
          ]}
          relatedLinks={[
            { kicker: "Upholstery", label: "Re-Upholstery Service", to: "/services" },
            { kicker: "Soft Furnishings", label: "Curtains & Blinds Dubai", to: "/curtains-dubai" },
            { kicker: "Joinery", label: "Custom Wardrobes Dubai", to: "/custom-wardrobes-dubai" },
            { kicker: "Turnkey", label: "Interior Fit-Out Dubai", to: "/interior-fit-out-dubai" },
          ]}
        />
        </>
      }
      internalLinksLine={
        <>
          Also see our{" "}
          <InternalLink to="/custom-wardrobes-dubai">custom wardrobes</InternalLink>
          ,{" "}
          <InternalLink to="/curtains-dubai">curtains & blinds</InternalLink>
          , or our{" "}
          <InternalLink to="/services">full interior fit-out services</InternalLink>
          .
        </>
      }
    />
  );
}
